import BenchmarkUploadHelp, { benchmarkUploadIssues } from "@/components/BenchmarkUploadHelp";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { API_BASE_URL } from "../config/api";
import AdminGuard, { useCanEdit } from "@/components/AdminGuard";
import { TOSS_LOGO_URL as LOGO_URL, BRAND } from "@/lib/branding";
import { PARAM_UNITS, advancedUnit } from "@/lib/reportUtils";
import ReferenceBasis, { basisUnit } from "@/components/ReferenceBasis";
import MaterialEvidence from "@/components/MaterialEvidence";

const API = API_BASE_URL;

const PARAM_KEYS = ["gwp", "water", "pollution", "circularity", "risk", "social", "ecosystem", "economic", "certifications", "time", "cost", "governance"];
const PARAM_LABELS = {
  gwp: "GWP Climate Impact", water: "Water Impact", pollution: "Pollution Burden",
  circularity: "Material Circularity", risk: "Operational Safety", social: "Social Responsibility",
  ecosystem: "Ecosystem Impact", economic: "Economic Viability", certifications: "Certifications & Ethics",
  time: "Estimated Time", cost: "Estimated Cost", governance: "Governance"
};
const PHASES = ["extraction", "processing", "disposal"];
const PHASE_LABELS = { extraction: "Extraction", processing: "Processing", disposal: "Disposal" };

export default function MaterialBenchmarks() {
  const [importIssues, setImportIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [references, setReferences] = useState({world:{},country:{}});
  const [worldAvg, setWorldAvg] = useState({});
  const [countryData, setCountryData] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [activeTab, setActiveTab] = useState("world_avg");
  const worldFileRef = useRef(null);
  const countryFileRef = useRef(null);
  const canEdit = useCanEdit();

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const loadAll = async () => {
    try {
      const [wRes, cRes, rRes] = await Promise.all([
        axios.get(`${API}/material-benchmarks`),
        axios.get(`${API}/country-material-data`),
        axios.get(`${API}/material-reference-bases`),
      ]);
      setReferences(rRes.data);
      setWorldAvg(wRes.data);
      setCountryData(cRes.data);
      const matNames = Object.keys(wRes.data);
      if (matNames.length > 0 && !selectedMaterial) setSelectedMaterial(matNames[0]);
      const ctNames = Object.keys(cRes.data);
      if (ctNames.length > 0 && !selectedCountry) setSelectedCountry(ctNames[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateWorldVal = (phase, param, value) => {
    setWorldAvg(prev => ({
      ...prev,
      [selectedMaterial]: {
        ...prev[selectedMaterial],
        [phase]: { ...(prev[selectedMaterial]?.[phase] || {}), [param]: value === '' ? null : Number(value) }
      }
    }));
  };

  const updateCountryVal = (phase, param, value) => {
    setCountryData(prev => ({
      ...prev,
      [selectedCountry]: {
        ...prev[selectedCountry],
        [selectedMaterial]: {
          ...(prev[selectedCountry]?.[selectedMaterial] || {}),
          [phase]: { ...(prev[selectedCountry]?.[selectedMaterial]?.[phase] || {}), [param]: value === '' ? null : Number(value) }
        }
      }
    }));
  };

  const getReference = (phase, country) => (country ? references.country?.[selectedCountry]?.[selectedMaterial]?.[phase] : references.world?.[selectedMaterial]?.[phase]) || {};
  const setReference = (phase, country, reference) => setReferences(old => country ? {...old,country:{...old.country,[selectedCountry]:{...old.country?.[selectedCountry],[selectedMaterial]:{...old.country?.[selectedCountry]?.[selectedMaterial],[phase]:reference}}}} : {...old,world:{...old.world,[selectedMaterial]:{...old.world?.[selectedMaterial],[phase]:reference}}});

  const saveWorldPhase = async (phase) => {
    try {
      await axios.put(`${API}/material-benchmarks`, { material: selectedMaterial, phase, values: worldAvg[selectedMaterial]?.[phase] || {}, units: PARAM_UNITS, reference: {basis:"per_kg"} });
      toast.success(`Saved World Average - ${selectedMaterial} ${PHASE_LABELS[phase]}`);
    } catch (e) { toast.error("Save failed"); }
  };

  const saveCountryPhase = async (phase) => {
    try {
      await axios.put(`${API}/country-material-data`, { country: selectedCountry, material: selectedMaterial, phase, values: countryData[selectedCountry]?.[selectedMaterial]?.[phase] || {}, units: PARAM_UNITS, reference: {basis:"per_kg"} });
      toast.success(`Saved ${selectedCountry} - ${selectedMaterial} ${PHASE_LABELS[phase]}`);
    } catch (e) { toast.error("Save failed"); }
  };

  const handleWorldImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportIssues([]);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/import-world-average`, fd);
      toast.success(`Imported ${res.data.count} world average records`);
      await loadAll();
    } catch (err) { setImportIssues(benchmarkUploadIssues(err)); toast.error("Upload not saved. See the field details above."); }
    if (worldFileRef.current) worldFileRef.current.value = "";
  };

  const handleCountryImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportIssues([]);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/import-country-data`, fd);
      toast.success(`Imported ${res.data.count} country data records`);
      await loadAll();
    } catch (err) { setImportIssues(benchmarkUploadIssues(err)); toast.error("Upload not saved. See the field details above."); }
    if (countryFileRef.current) countryFileRef.current.value = "";
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  const countryMaterials = Object.values(countryData).flatMap(data => Object.keys(data || {}));
  const matList = [...new Set([...Object.keys(worldAvg), ...countryMaterials])].sort();

  return (
    <div className="p-8 max-w-6xl" data-testid="material-benchmarks-page">
      <BenchmarkUploadHelp issues={importIssues} />
      <input type="file" ref={worldFileRef} accept=".xlsx" onChange={handleWorldImport} className="hidden" />
      <input type="file" ref={countryFileRef} accept=".xlsx" onChange={handleCountryImport} className="hidden" />

      <div className="flex items-center gap-3 mb-2">
        <img src={LOGO_URL} alt={BRAND.short} className="h-8 w-auto object-contain" />
        <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Material Benchmarks</h1>
      </div>
      <p className="text-sm text-[#617364] mb-4">Step 3A: World Average and Country-specific material data for Extraction, Processing, and Disposal phases</p>
      <AdminGuard resource="material benchmarks" />

      {/* Material selector */}
      <div className="flex items-end gap-4 mb-6">
        <div className="flex-1 max-w-xs">
          <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Material</Label>
          <Select value={selectedMaterial} onValueChange={setSelectedMaterial}>
            <SelectTrigger className="mt-1 border-[#E0E6E1]" data-testid="select-material"><SelectValue placeholder="Select material" /></SelectTrigger>
            <SelectContent>{matList.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {activeTab === "country" && (
          <div className="flex-1 max-w-xs">
            <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Country</Label>
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="mt-1 border-[#E0E6E1]" data-testid="select-country"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>{Object.keys(countryData).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F8F9F8] border border-[#E0E6E1] p-1 mb-4">
          <TabsTrigger value="world_avg" className="data-[state=active]:bg-white data-[state=active]:text-[#43A047]" data-testid="tab-world-avg">World Average Benchmark</TabsTrigger>
          <TabsTrigger value="country" className="data-[state=active]:bg-white data-[state=active]:text-[#43A047]" data-testid="tab-country">Country Material Data</TabsTrigger>
        </TabsList>

        <TabsContent value="world_avg">
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/template/world-average`, '_blank')} className="border-[#E0E6E1] text-[#617364]" data-testid="download-world-template">
              <Download className="w-3.5 h-3.5 mr-1" /> Download Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => worldFileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]" data-testid="import-world-avg-btn">
              <Upload className="w-3.5 h-3.5 mr-1" /> Import World Average
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/export-material-benchmarks`, '_blank')} className="border-[#E0E6E1] text-[#1B291D]" data-testid="export-all-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Export All
            </Button>
          </div>
          {selectedMaterial && PHASES.map(phase => (
            <Card key={phase} className="border-[#E0E6E1] bg-white mb-4">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{selectedMaterial} - {PHASE_LABELS[phase]}</CardTitle>
                  <Button onClick={() => saveWorldPhase(phase)} disabled={!canEdit} size="sm" className="bg-[#43A047] hover:bg-[#388E3C] text-white disabled:opacity-50" data-testid={`save-world-${phase}`} title={!canEdit ? "Admin access required" : ""}><Save className="w-3 h-3 mr-1" /> Save</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {PARAM_KEYS.map(pk => (
                    <div key={pk}>
                      <Label className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold">{PARAM_LABELS[pk]}</Label><p className="text-[10px] text-[#617364]">{advancedUnit(basisUnit(pk,getReference(phase,activeTab === "country"),true) || getReference(phase,activeTab === "country").source_units?.[pk] || PARAM_UNITS[pk])}</p>
                      <Input type="number" step="any" value={worldAvg[selectedMaterial]?.[phase]?.[pk] ?? ""} onChange={e => updateWorldVal(phase, pk, e.target.value)}
                        className="border-[#E0E6E1] h-8 text-sm mt-1" data-testid={`world-${phase}-${pk}`} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="country">
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => window.open(`${API}/template/country-data`, '_blank')} className="border-[#E0E6E1] text-[#617364]" data-testid="download-country-template">
              <Download className="w-3.5 h-3.5 mr-1" /> Download Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => countryFileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]" data-testid="import-country-btn">
              <Upload className="w-3.5 h-3.5 mr-1" /> Import Country Data
            </Button>
          </div>
          {selectedMaterial && selectedCountry && PHASES.map(phase => (
            <Card key={phase} className="border-[#E0E6E1] bg-white mb-4">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{selectedCountry} - {selectedMaterial} - {PHASE_LABELS[phase]}</CardTitle>
                  <Button onClick={() => saveCountryPhase(phase)} disabled={!canEdit} size="sm" className="bg-[#43A047] hover:bg-[#388E3C] text-white disabled:opacity-50" data-testid={`save-country-${phase}`} title={!canEdit ? "Admin access required" : ""}><Save className="w-3 h-3 mr-1" /> Save</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {PARAM_KEYS.map(pk => (
                    <div key={pk}>
                      <Label className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold">{PARAM_LABELS[pk]}</Label><p className="text-[10px] text-[#617364]">{advancedUnit(basisUnit(pk,getReference(phase,activeTab === "country"),true) || getReference(phase,activeTab === "country").source_units?.[pk] || PARAM_UNITS[pk])}</p>
                      <Input type="number" step="any" value={countryData[selectedCountry]?.[selectedMaterial]?.[phase]?.[pk] ?? ""} onChange={e => updateCountryVal(phase, pk, e.target.value)}
                        className="border-[#E0E6E1] h-8 text-sm mt-1" data-testid={`country-${phase}-${pk}`} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}